import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ============================================================
   THÈMES
   ============================================================ */
const THEMES = {
  dark: {
    page:         { bg: "#0a0a0a", color: "#fff" },
    card:         { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.08)" },
    cardHeader:   { bg: "rgba(230,255,62,0.07)", border: "rgba(230,255,62,0.15)", color: "#e6ff3e" },
    toolbar:      { bg: "rgba(255,255,255,0.02)", border: "rgba(255,255,255,0.06)" },
    toolbarLabel: { color: "#64748b" },
    select:       { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", color: "#fff" },
    input:        { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", color: "#fff" },
    th:           { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.08)", color: "#94a3b8" },
    td:           { bg: "transparent", border: "rgba(255,255,255,0.05)", color: "#e2e8f0" },
    tdAlt:        { bg: "rgba(255,255,255,0.02)" },
    emptyCell:    { color: "#64748b" },
    sectionTitle: { color: "#fff" },
    toggleBtn:    { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.12)", color: "#fff" },
    accent:       "#e6ff3e",
    accentText:   "#000",
    resetBtn:     { bg: "rgba(255,255,255,0.06)", border: "rgba(255,255,255,0.1)", color: "#fff" },
    conformite: {
      delamine:    { color: "#f87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.3)" },
      nonconforme: { color: "#fb923c", bg: "rgba(251,146,60,0.1)",  border: "rgba(251,146,60,0.3)"  },
      conforme:    { color: "#4ade80", bg: "rgba(74,222,128,0.1)",  border: "rgba(74,222,128,0.3)"  },
      default:     { color: "#94a3b8", bg: "transparent",           border: "transparent"            },
    },
  },
  light: {
    page:         { bg: "#f1f5f9", color: "#0f172a" },
    card:         { bg: "#fff", border: "#e2e8f0" },
    cardHeader:   { bg: "#f8fafc", border: "#e2e8f0", color: "#475569" },
    toolbar:      { bg: "#f8fafc", border: "#e2e8f0" },
    toolbarLabel: { color: "#475569" },
    select:       { bg: "#fff", border: "#e2e8f0", color: "#0f172a" },
    input:        { bg: "#fff", border: "#e2e8f0", color: "#0f172a" },
    th:           { bg: "#f1f5f9", border: "#e2e8f0", color: "#64748b" },
    td:           { bg: "#fff", border: "#f1f5f9", color: "#334155" },
    tdAlt:        { bg: "#f8fafc" },
    emptyCell:    { color: "#94a3b8" },
    sectionTitle: { color: "#0f172a" },
    toggleBtn:    { bg: "#fff", border: "#e2e8f0", color: "#334155" },
    accent:       "#1d4ed8",
    accentText:   "#fff",
    resetBtn:     { bg: "#fff", border: "#e2e8f0", color: "#334155" },
    conformite: {
      delamine:    { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
      nonconforme: { color: "#c2410c", bg: "#fff7ed", border: "#fdba74" },
      conforme:    { color: "#166534", bg: "#f0fdf4", border: "#86efac" },
      default:     { color: "#94a3b8", bg: "transparent", border: "transparent" },
    },
  },
};

/* ============================================================
   INJECT CSS DYNAMIQUE
   ============================================================ */
function injectStyles(mode) {
  let el = document.getElementById("cilss-dynamic-styles");
  if (!el) {
    el = document.createElement("style");
    el.id = "cilss-dynamic-styles";
    document.head.appendChild(el);
  }
  const lime         = mode === "dark" ? "rgba(230,255,62,0.5)"  : "#1d4ed8";
  const limeShadow   = mode === "dark" ? "rgba(230,255,62,0.1)"  : "rgba(29,78,216,0.1)";
  const btnHover     = mode === "dark" ? "#fff"                  : "#2563eb";
  const btnHoverTx   = mode === "dark" ? "#000"                  : "#fff";
  const btnShadow    = mode === "dark" ? "rgba(230,255,62,0.25)" : "rgba(29,78,216,0.25)";
  const trHover      = mode === "dark" ? "rgba(255,255,255,0.03)": "#f8fafc";
  const inputFocusBg = mode === "dark" ? "rgba(230,255,62,0.04)" : "rgba(29,78,216,0.04)";

  el.innerHTML = `
    .cilss-input:focus, .cilss-select:focus {
      border-color: ${lime} !important;
      box-shadow: 0 0 0 3px ${limeShadow} !important;
      background: ${inputFocusBg} !important;
      outline: none;
    }
    .cilss-btn-accent:hover:not(:disabled) {
      background: ${btnHover} !important;
      color: ${btnHoverTx} !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 6px 20px ${btnShadow} !important;
    }
    .cilss-btn-accent:active { transform: scale(0.97) !important; }
    .cilss-btn-reset:hover   { opacity: 0.75; }
    .cilss-toggle-btn:hover  { opacity: 0.8; transform: scale(1.05); }
    .cilss-tr:hover td       { background: ${trHover} !important; }
    .cilss-action-btn:hover  {
      background: ${btnHover} !important;
      color: ${btnHoverTx} !important;
      transform: scale(1.03) !important;
    }
    @media (min-width: 640px)  { .cilss-wrap { padding: 24px 32px 48px !important; } }
    @media (min-width: 900px)  { .cilss-wrap { padding: 28px 60px 48px !important; } }
    @media (min-width: 1200px) { .cilss-wrap { padding: 32px 140px 48px !important; } }
  `;
}

/* ============================================================
   HELPERS
   ============================================================ */
function getConformiteStyle(val, T) {
  if (!val) return T.conformite.default;
  const v = String(val).toUpperCase();
  if (v.includes("DÉLAMINÉ") || v.includes("DELAMINE")) return T.conformite.delamine;
  if (v.includes("NON CONFORME"))                        return T.conformite.nonconforme;
  if (v.includes("CONFORME"))                            return T.conformite.conforme;
  return T.conformite.default;
}

function applyFilter(arr, key, value) {
  if (!key || String(value).trim() === "") return arr;
  const needle = String(value).toLowerCase();
  return arr.filter((r) => r[key] != null && String(r[key]).toLowerCase().includes(needle));
}

function asFrNumber(val) {
  if (val == null) return "";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString("fr-FR", { useGrouping: false }) : val;
}

/* ✅ Formate le timestamp ISO/epoch en date lisible fr-FR */
function formatTimestamp(val) {
  if (val == null || val === "") return null;
  const asNum = Number(val);
  const date = Number.isFinite(asNum) && asNum > 1_000_000_000
    ? new Date(asNum < 1e12 ? asNum * 1000 : asNum) // epoch secondes ou ms
    : new Date(val);                                  // string ISO
  if (isNaN(date.getTime())) return String(val);      // fallback si invalide
  return date.toLocaleString("fr-FR", {
    day:    "2-digit",
    month:  "2-digit",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  });
}

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ConsultationILSS() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];
  useEffect(() => { injectStyles(mode); localStorage.setItem("qsf-theme", mode); }, [mode]);
  const toggleMode = () => setMode((m) => (m === "dark" ? "light" : "dark"));

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState("");

  const [sortKey,      setSortKey]      = useState("index");
  const [sortDir,      setSortDir]      = useState("asc");
  const [filterKey,    setFilterKey]    = useState("");
  const [filterValue,  setFilterValue]  = useState("");
  const [filterKey2,   setFilterKey2]   = useState("");
  const [filterValue2, setFilterValue2] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r    = await apiFetch("/api/ilss/list");
        if (!r.ok) throw new Error("Erreur serveur");
        const data = await r.json();
        console.log("=== RAW DATA[0] ===", JSON.stringify(data[0], null, 2));
console.log("=== TOUTES LES CLÉS ===", Object.keys(data[0] || {}));

        const mapped = (data || []).map((it) => {
          // ✅ Robustesse casse : supporte Timestamp / timestamp / TIMESTAMP
          const rawTs = it.Timestamp ?? it.timestamp ?? it.TIMESTAMP ?? null;
          return {
            index:            it.index,
            Timestamp:        rawTs,                  // valeur brute pour tri/filtre
            TimestampDisplay: formatTimestamp(rawTs), // valeur formatée pour affichage
            Operator:         it.Operator,
            ID_Stack:         it.ID_Stack,
            No_Test:          it.No_Test,
            Typologie:        it.Typologie,
            Largeur:          it.Largeur,
            Epaisseur:        it.Epaisseur,
            Force:            it.Force,
              Typ_Rupt:         it.Typ_Rupt,
            ILS_BDB_MPa:      it.ILS_BDB_MPa,
            ILS_72h_MPa:      it.ILS_72h_MPa,
            Conformite_ILSS:  it.Conformite_ILSS,
            raw:              it,
          };
        });

        if (!cancelled) setRows(mapped);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const view = useMemo(() => {
    let out = applyFilter([...rows], filterKey, filterValue);
    out     = applyFilter(out, filterKey2, filterValue2);
    const dir = sortDir === "asc" ? 1 : -1;
    out.sort((a, b) => String(a[sortKey]).localeCompare(String(b[sortKey])) * dir);
    return out;
  }, [rows, sortKey, sortDir, filterKey, filterValue, filterKey2, filterValue2]);

  const handleEdit = (raw) => {
    localStorage.setItem("woodoo_edit_item", JSON.stringify(raw));
    navigate("/qualite/controleStack/ilss", { state: { item: raw, edit: true } });
  };

  const resetFilters = () => {
    setFilterKey(""); setFilterValue("");
    setFilterKey2(""); setFilterValue2("");
    setSortKey("index"); setSortDir("asc");
  };

  const s = {
    page:         { minHeight: "100vh", background: T.page.bg, color: T.page.color, fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif", transition: "background 0.3s,color 0.3s" },
    wrap:         { padding: "20px 16px 48px" },
    topBar:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, marginTop: 8 },
    sectionLeft:  { display: "flex", alignItems: "center", gap: 12 },
    sectionBadge: { background: T.accent, color: T.accentText, fontWeight: 900, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 4 },
    sectionTitle: { fontWeight: 900, fontSize: 20, letterSpacing: "0.04em", textTransform: "uppercase", color: T.sectionTitle.color },
    toggleBtn:    { display: "flex", alignItems: "center", gap: 8, background: T.toggleBtn.bg, border: `1px solid ${T.toggleBtn.border}`, color: T.toggleBtn.color, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", transition: "all 0.2s" },
    card:         { background: T.card.bg, border: `1px solid ${T.card.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", transition: "all 0.3s" },
    cardHeader:   { padding: "14px 20px", background: T.cardHeader.bg, borderBottom: `1px solid ${T.cardHeader.border}`, fontWeight: 800, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: T.cardHeader.color },
    toolbar:      { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${T.toolbar.border}`, background: T.toolbar.bg },
    group:        { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" },
    toolLabel:    { fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.toolbarLabel.color },
    select:       { height: 34, background: T.select.bg, border: `1px solid ${T.select.border}`, borderRadius: 8, color: T.select.color, padding: "0 10px", fontSize: 12, fontFamily: "inherit", outline: "none", transition: "all 0.2s" },
    input:        { height: 34, background: T.input.bg, border: `1px solid ${T.input.border}`, borderRadius: 8, color: T.input.color, padding: "0 10px", fontSize: 12, fontFamily: "inherit", outline: "none", width: 120, transition: "all 0.2s" },
    btnAccent:    { height: 34, padding: "0 14px", border: "none", background: T.accent, color: T.accentText, fontWeight: 800, fontSize: 12, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    btnReset:     { height: 34, padding: "0 14px", background: T.resetBtn.bg, border: `1px solid ${T.resetBtn.border}`, color: T.resetBtn.color, fontWeight: 700, fontSize: 12, borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    tableWrap:    { overflowX: "auto" },
    table:        { width: "100%", borderCollapse: "collapse" },
    th:           { background: T.th.bg, color: T.th.color, fontWeight: 700, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: `1px solid ${T.th.border}`, padding: "12px 10px", whiteSpace: "nowrap" },
    // ✅ On retire whiteSpace nowrap sur td pour que le timestamp puisse s'afficher sur une ligne
    td:           { borderBottom: `1px solid ${T.td.border}`, padding: "10px 10px", fontSize: 13, color: T.td.color, whiteSpace: "nowrap" },
    // ✅ td spéciale Timestamp avec minWidth pour ne pas écraser la valeur
    tdTimestamp:  { borderBottom: `1px solid ${T.td.border}`, padding: "10px 10px", fontSize: 13, color: T.td.color, whiteSpace: "nowrap", minWidth: 140 },
    emptyCell:    { padding: 32, textAlign: "center", color: T.emptyCell.color, fontSize: 14 },
    actionBtn:    { background: T.accent, color: T.accentText, fontWeight: 800, fontSize: 11, letterSpacing: "0.05em", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    error:        { color: "#f87171", padding: "12px 16px", fontWeight: 700, fontSize: 13 },
    conformeBadge: (val) => {
      const c = getConformiteStyle(val, T);
      return { display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: c.bg, border: `1px solid ${c.border}`, color: c.color };
    },
  };

  const SORT_OPTIONS = [
    { value: "index",     label: "Index"     },
    { value: "Timestamp", label: "Timestamp" },
    { value: "Operator",  label: "Opérateur" },
    { value: "ID_Stack",  label: "Stack"     },
    { value: "No_Test",   label: "N° Test"   },
    { value: "Typologie", label: "Typologie" },
  ];
  const FILTER_OPTIONS = [
    { value: "Timestamp",      label: "Timestamp" },
    { value: "Operator",       label: "Opérateur" },
    { value: "ID_Stack",       label: "Stack"     },
    { value: "Typologie",      label: "Typologie" },
    { value: "Conformite_ILSS",label: "Conformité"},
  ];
  const FILTER2_OPTIONS = [
    { value: "ID_Stack",        label: "Stack"     },
    { value: "Typologie",       label: "Typologie" },
    { value: "Conformite_ILSS", label: "Conformité"},
  ];

  const COLS = [
    "INDEX","TIMESTAMP","OPÉRATEUR","ID STACK","N° TEST","TYPOLOGIE",
    "LARGEUR","ÉPAISSEUR","FORCE","ILS BDB (MPa)","ILS 72h (MPa)","TYP. RUPT.","CONFORMITÉ","ACTIONS",
  ];

 return (
    <div style={s.page}>
      <MenuBar active="qualite" />

      <div style={s.wrap} className="cilss-wrap">

        {/* ── Top bar ── */}
        <div style={s.topBar}>
          <div style={s.sectionLeft}>
            <span style={s.sectionBadge}>Qualité</span>
            <span style={s.sectionTitle}>Consultation ILSS</span>
          </div>
          <button style={s.toggleBtn} className="cilss-toggle-btn" onClick={toggleMode}>
            <span style={{ fontSize: 16 }}>{mode === "dark" ? "☀️" : "🌙"}</span>
            <span>{mode === "dark" ? "Clair" : "Sombre"}</span>
          </button>
        </div>

        {/* ── Card ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>Mesures ILSS</div>

          {/* ── Toolbar ── */}
          <div style={s.toolbar}>

            {/* Tri */}
            <div style={s.group}>
              <span style={s.toolLabel}>Trier</span>
              <select style={s.select} className="cilss-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button style={s.btnAccent} className="cilss-btn-accent" onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}>
                {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
              </button>
            </div>

            {/* Filtre 1 */}
            <div style={s.group}>
              <span style={s.toolLabel}>Filtrer</span>
              <select style={s.select} className="cilss-select" value={filterKey} onChange={(e) => { setFilterKey(e.target.value); setFilterValue(""); }}>
                <option value="">{ui("— colonne —")}</option>
                {FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={s.input} className="cilss-input" value={filterValue} onChange={(e) => setFilterValue(e.target.value)} placeholder="valeur…" />
            </div>

            {/* Filtre 2 */}
            <div style={s.group}>
              <span style={s.toolLabel}>Et</span>
              <select style={s.select} className="cilss-select" value={filterKey2} onChange={(e) => { setFilterKey2(e.target.value); setFilterValue2(""); }}>
                <option value="">{ui("— colonne —")}</option>
                {FILTER2_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={s.input} className="cilss-input" value={filterValue2} onChange={(e) => setFilterValue2(e.target.value)} placeholder="valeur…" />
            </div>

            <button style={s.btnReset} className="cilss-btn-reset" onClick={resetFilters}>
              ✕ Réinitialiser
            </button>
          </div>

          {/* ── Compteur ── */}
          <div style={{ padding: "8px 16px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: T.toolbarLabel.color, borderBottom: `1px solid ${T.toolbar.border}` }}>
            {loading ? "Chargement…" : `${view.length} résultat${view.length > 1 ? "s" : ""}`}
          </div>

          {/* ── Table ── */}
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {COLS.map((c) => <th key={c} style={s.th}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={COLS.length} style={s.emptyCell}>{ui("Chargement…")}</td></tr>
                )}

                {!loading && view.map((r, i) => (
                  <tr key={i} className="cilss-tr" style={{ background: i % 2 === 1 ? T.tdAlt.bg : "transparent" }}>

                    {/* INDEX */}
                    <td style={{ ...s.td, textAlign: "center" }}>{r.index ?? "—"}</td>

                    {/* TIMESTAMP */}
                    <td style={{ ...s.tdTimestamp, textAlign: "center" }} title={r.Timestamp ?? ""}>
                      {r.TimestampDisplay ?? "—"}
                    </td>

                    {/* OPÉRATEUR */}
                    <td style={{ ...s.td, textAlign: "center" }}>{r.Operator ?? "—"}</td>

                    {/* ID STACK */}
                    <td style={{ ...s.td, textAlign: "center" }}>{r.ID_Stack ?? "—"}</td>

                    {/* N° TEST */}
                    <td style={{ ...s.td, textAlign: "center" }}>{r.No_Test ?? "—"}</td>

                    {/* TYPOLOGIE */}
                    <td style={{ ...s.td, textAlign: "center" }}>{r.Typologie ?? "—"}</td>

                    {/* LARGEUR */}
                    <td style={{ ...s.td, textAlign: "center" }}>{asFrNumber(r.Largeur) || "—"}</td>

                    {/* ÉPAISSEUR */}
                    <td style={{ ...s.td, textAlign: "center" }}>{asFrNumber(r.Epaisseur) || "—"}</td>

                    {/* FORCE */}
                    <td style={{ ...s.td, textAlign: "center" }}>{asFrNumber(r.Force) || "—"}</td>

                    {/* ILS BDB */}
                    <td style={{ ...s.td, textAlign: "center" }}>{asFrNumber(r.ILS_BDB_MPa) || "—"}</td>

                    {/* ILS 72h */}
                    <td style={{ ...s.td, textAlign: "center" }}>{asFrNumber(r.ILS_72h_MPa) || "—"}</td>
                       {/* TYP. RUPT. */}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      {r.Typ_Rupt ?? "—"}
                    </td>

                    {/* CONFORMITÉ badge */}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      <span style={s.conformeBadge(r.Conformite_ILSS)}>
                        {r.Conformite_ILSS || "—"}
                      </span>
                    </td>



                    {/* ACTIONS */}
                    <td style={{ ...s.td, textAlign: "center" }}>
                      <button style={s.actionBtn} className="cilss-action-btn" onClick={() => handleEdit(r.raw)}>
                        {ui("Modifier")}
                      </button>
                    </td>
                  </tr>
                ))}

                {!loading && view.length === 0 && (
                  <tr><td colSpan={COLS.length} style={s.emptyCell}>Aucune donnée</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {err && <div style={s.error}>⚠ {err}</div>}
        </div>
      </div>
    </div>
  );
}